import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { LocalAuthService } from 'src/app/auth/local-auth.service';
import { TrackJsService } from 'src/app/services/track-js.service';
import { catchError, of, switchMap } from 'rxjs';

/**
 * Requires a valid staff JWT (same bar as ColdFusion seasonal: any `session.staffinfo`-class access).
 * No extra role checks — only `LocalAuthService.currentUser` with `profileType === 'staff'`.
 */
export const mcTripsStaffGuard: CanActivateFn = (route, state) => {
  const authService = inject(LocalAuthService);
  const router = inject(Router);
  const trackJs = inject(TrackJsService);

  function redirectToLogin(): boolean {
    const reason = authService.getLoggedInFailureReason();
    trackJs.consoleWarn('[Auth] MC Trips: redirecting to login', { reason, attemptedUrl: state.url });
    authService.clearToken();
    void router.navigate(['/login'], {
      skipLocationChange: true,
      queryParams: { returnRoute: state.url },
    });
    return false;
  }

  function isStaff(): boolean {
    const user = authService.currentUser;
    return !!user && user.profileType === 'staff';
  }

  if (!authService.loggedIn) {
    if (!authService.isRefreshTokenExpired()) {
      return authService.refreshLogin().pipe(
        switchMap(() => of(isStaff() ? true : redirectToLogin())),
        catchError(() => of(redirectToLogin())),
      );
    }
    return of(redirectToLogin());
  }

  if (!isStaff()) {
    trackJs.consoleWarn('[Auth] MC Trips: session is not a staff profile; clearing and redirecting to login', {
      attemptedUrl: state.url,
    });
    return of(redirectToLogin());
  }

  return of(true);
};
